import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaPlus, FaCogs,
  FaGraduationCap, FaBookOpen, FaStar, FaClock, FaTimesCircle
} from 'react-icons/fa';
import api from '../services/api';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [marksProgress, setMarksProgress] = useState(null);
  const [marksLoading, setMarksLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== 'student') return;

    const fetchMarksProgress = async () => {
      try {
        setMarksLoading(true);
        const res = await api.get('/submissions/my-progress');
        if (res.data.success) {
          setMarksProgress(res.data.data);
        }
      } catch (err) {
        console.error('Failed to load marks progress', err);
      } finally {
        setMarksLoading(false);
      }
    };

    fetchMarksProgress();
  }, [user?.role]);

  const totalMarks = marksProgress?.totalMarks || 0;
  const totalPossibleMarks = marksProgress?.totalPossibleMarks || 0;
  const progressPercentage = marksProgress?.progressPercentage || 0;

  return (
    <div className="min-h-screen bg-maroon-dark text-white p-6 md:p-12 pt-24 relative overflow-hidden">
      {/* Background blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-gold/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-gold/5 blur-[120px] rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <header className="mb-10">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-gradient-gold">
            Namaste, {user?.name}!
          </h1>
          <p className="text-gold-light/50 mt-2 text-lg font-sans">
            Welcome to your {user?.role === 'student' ? 'Learning Space' : 'Teaching Hub'}
          </p>
        </header>

        {user?.role === 'student' ? (
          <>
            {/* Action Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-10">
              <div className="bg-white/5 backdrop-blur-md border border-gold/10 p-8 rounded-2xl flex flex-col">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center">
                    <FaStar className="text-3xl text-gold" />
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-widest text-gold-light/40 font-semibold">Total Marks</p>
                    <p className="text-3xl font-bold text-gold-light">
                      {marksLoading ? '...' : totalMarks}
                      <span className="text-base text-gold-light/40 font-medium">/{totalPossibleMarks}</span>
                    </p>
                  </div>
                </div>
                <h3 className="text-2xl font-serif font-bold text-gold-light mb-4">My Progress</h3>
                <div className="mb-5">
                  <div className="flex items-center justify-between text-xs text-gold-light/50 mb-2">
                    <span>Approved video marks</span>
                    <span>{progressPercentage}%</span>
                  </div>
                  <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gold rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="bg-white/5 rounded-xl p-3">
                    <p className="text-lg font-bold text-green-300">{marksProgress?.approvedCount || 0}</p>
                    <p className="text-gold-light/40">Approved</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3">
                    <p className="text-lg font-bold text-yellow-300">{marksProgress?.pendingCount || 0}</p>
                    <p className="text-gold-light/40">Pending</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3">
                    <p className="text-lg font-bold text-red-300">{marksProgress?.rejectedCount || 0}</p>
                    <p className="text-gold-light/40">Rejected</p>
                  </div>
                </div>
              </div>

              <div
                onClick={() => navigate('/lessons')}
                className="bg-white/5 backdrop-blur-md border border-gold/10 p-8 rounded-2xl flex flex-col group hover:bg-white/10 hover:border-gold/25 transition-all cursor-pointer"
              >
                <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <FaGraduationCap className="text-3xl text-gold" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-gold-light mb-4">My Lessons</h3>
                <p className="text-gold-light/50 mb-8 flex-grow font-sans">
                  View lessons, watch videos and submit your practice recordings.
                </p>
                <span className="w-full py-4 btn-gold !rounded-xl text-center block">Go to Lessons</span>
              </div>

              <div
                onClick={() => navigate('/homework')}
                className="bg-white/5 backdrop-blur-md border border-gold/10 p-8 rounded-2xl flex flex-col group hover:bg-white/10 hover:border-gold/25 transition-all cursor-pointer"
              >
                <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <FaBookOpen className="text-3xl text-gold" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-gold-light mb-4">My Homework</h3>
                <p className="text-gold-light/50 mb-8 flex-grow font-sans">
                  View assigned tasks and submit your practice videos for review.
                </p>
                <span className="w-full py-4 btn-gold !rounded-xl text-center block">View Assignments</span>
              </div>
            </div>

            {marksProgress?.recentSubmissions?.length > 0 && (
              <div className="bg-white/5 backdrop-blur-md border border-gold/10 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-xl font-serif font-bold text-gold-light">Recent Video Marks</h3>
                  <button
                    onClick={() => navigate('/lessons')}
                    className="text-sm text-gold hover:text-gold-light transition"
                  >
                    View lessons
                  </button>
                </div>
                <div className="space-y-3">
                  {marksProgress.recentSubmissions.map(submission => (
                    <div key={submission._id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-gold/10 bg-black/10 px-4 py-3">
                      <div>
                        <p className="font-semibold text-gold-light">{submission.video?.title || 'Practice video'}</p>
                        <p className="text-sm text-gold-light/40">{submission.lesson?.title || 'Lesson'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {submission.status === 'approved' && (
                          <span className="px-3 py-1 rounded-full bg-green-500/15 text-green-300 text-sm font-bold">
                            {submission.grade || 0}/10
                          </span>
                        )}
                        {submission.status === 'pending' && (
                          <span className="px-3 py-1 rounded-full bg-yellow-500/15 text-yellow-300 text-sm font-semibold flex items-center gap-1">
                            <FaClock className="text-xs" /> Pending
                          </span>
                        )}
                        {submission.status === 'rejected' && (
                          <span className="px-3 py-1 rounded-full bg-red-500/15 text-red-300 text-sm font-semibold flex items-center gap-1">
                            <FaTimesCircle className="text-xs" /> Rejected
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          /* Teacher / Admin cards */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: FaPlus, title: 'Create Content', desc: 'Upload new lessons for students.', link: '/admin/dashboard', label: 'New Lesson' },
              { icon: FaCogs, title: 'Manage Classes', desc: 'Organize your existing curriculum.', link: '/admin/dashboard', label: 'Dashboard' },
              { icon: FaGraduationCap, title: 'Students', desc: "View your students' progress.", link: '/admin/dashboard', label: 'Browse List' },
            ].map(({ icon: Icon, title, desc, link, label }) => (
              <div key={title} className="bg-white/5 backdrop-blur-md border border-gold/10 p-8 rounded-2xl flex flex-col group hover:bg-white/10 hover:border-gold/25 transition-all">
                <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Icon className="text-3xl text-gold" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-gold-light mb-4">{title}</h3>
                <p className="text-gold-light/50 mb-8 flex-grow font-sans">{desc}</p>
                <Link to={link} className="w-full py-4 btn-gold !rounded-xl text-center block">{label}</Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
